// Tile thumbnails live in the same bucket under thumbs/<key>, so the
// thumb URL is derived from the original's rather than stored. Mirrors
// keyFromImageURL in backend/config/s3.js.

const BUCKET_HOST = "the-japanese-stamp-archive.s3.ca-central-1.amazonaws.com";

// Thumb URL for a stored image URL, or null when one can't exist
// (legacy pre-migration URLs, foreign hosts). Callers should fall back
// to the original when this is null or the thumb 404s (not yet
// backfilled) — FramedImage handles the latter via onError.
export function thumbUrl(imageURL) {
  try {
    const url = new URL(imageURL);
    if (url.hostname !== BUCKET_HOST) return null;
    const key = url.pathname.slice(1);
    if (!key || key.startsWith("thumbs/")) return null;
    return `${url.origin}/thumbs/${key}`;
  } catch {
    return null;
  }
}

// Downscale an image file to a JPEG thumbnail blob (max `maxEdge` px on
// the long side) for the tile-thumbnail upload. Resolves null when the
// file can't be decoded or encoded (e.g. SVG quirks) — thumbnails are
// best-effort and callers must not fail the original upload over one.
// 2048 px (not the ~700 device px a tile renders at) because tile
// framing zoom crops in, showing only a fraction of the thumb — many
// tiles frame at 2-2.3x zoom, which 1600 px covered only borderline.
// Keep in sync with MAX_EDGE in backend/scripts/backfill-thumbnails.js.
export function makeThumbnailBlob(file, maxEdge = 2048, quality = 0.85) {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext("2d");
      // JPEG has no alpha; flatten any transparency onto white
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    img.src = objectUrl;
  });
}
